<template>
  <div class="task-page">
    <div class="background-layer">
      <div class="glow glow-left"></div>
      <div class="glow glow-right"></div>
      <div class="grid"></div>
    </div>

    <div class="page-shell">
      <header class="page-header reveal-up">
        <button class="back-btn prominent-back-btn" @click="goHome"><span>←</span><strong>Back to Home</strong><em>返回首页</em></button>
        <div>
          <p class="eyebrow">RoboCoin 任务发布</p>
          <h1>发布任务</h1>
        </div>
      </header>

      <main class="task-layout">
        <section class="publisher-workspace">
          <aside class="publisher-sidebar reveal-up">
            <section class="task-summary-card">
              <div class="summary-head">
                <div>
                  <p class="eyebrow">Task Summary</p>
                  <h2>任务摘要</h2>
                </div>
                <span>{{ completedSummaryItems }}/{{ sidebarSummaryItems.length }}</span>
              </div>
              <div class="summary-list">
                <div v-for="item in sidebarSummaryItems" :key="item.key" class="summary-item">
                  <i :class="{ ready: item.ready }"></i>
                  <div>
                    <span>{{ item.label }}</span>
                    <strong>{{ item.value }}</strong>
                  </div>
                </div>
              </div>
            </section>
          </aside>

          <section class="form-card reveal-up delay-2">
            <div class="card-title-row">
              <div>
                <p class="eyebrow">任务向导</p>
                <h3>{{ editingId ? '按步骤编辑任务' : '按步骤发布任务' }}</h3>
              </div>
              <button class="ghost-btn" @click="resetForm">重置</button>
            </div>

            <div class="progress-overview">
              <span>Step {{ currentStep }} of {{ guidedSteps.length }}</span>
              <strong>{{ activeQuestion.short }}</strong>
              <em>{{ editingId ? 'Editing mission' : 'New mission' }}</em>
            </div>

            <div class="flow-progress" :style="{ '--step-count': guidedSteps.length }">
              <button
                v-for="(step, index) in guidedSteps"
                :key="step.key"
                type="button"
                :class="['flow-dot', { active: currentStep === index + 1, done: currentStep > index + 1, locked: index + 1 > maxVisitedStep && index + 1 > currentStep }]"
                :disabled="index + 1 > maxVisitedStep && index + 1 > currentStep"
                :aria-label="`${index + 1}. ${step.short}`"
                :title="step.short"
                @click="goToStep(index + 1)"
              >
                <span>{{ index + 1 }}</span>
                <em>{{ step.short }}</em>
              </button>
            </div>

            <el-form ref="taskFormRef" :model="taskForm" :rules="taskRules" label-position="top" class="task-form">
              <transition name="question-slide" mode="out-in">
                <section :key="activeQuestion.key" class="question-panel">
                  <div class="step-heading">
                    <span>Step {{ paddedStep }}</span>
                    <h4>{{ activeQuestion.title }}</h4>
                    <p v-if="activeQuestion.help">{{ activeQuestion.help }}</p>
                  </div>

                  <el-form-item v-if="activeQuestion.key === 'serviceType'" prop="serviceType" class="choice-form-item">
                    <div class="choice-grid">
                      <button
                        v-for="item in serviceTypes"
                        :key="item.value"
                        type="button"
                        :class="['choice-card', { selected: taskForm.serviceType === item.value }]"
                        :aria-pressed="taskForm.serviceType === item.value"
                        @click.prevent="selectServiceType(item.value)"
                      >
                        <span>{{ item.badge }}</span>
                        <strong>{{ item.label }}</strong>
                        <p>{{ item.text }}</p>
                        <small v-if="taskForm.serviceType === item.value">已选择</small>
                      </button>
                    </div>
                  </el-form-item>

                  <el-form-item v-else-if="activeQuestion.key === 'expectedRobotType'" prop="expectedRobotType" class="choice-form-item">
                    <div class="choice-rail-head">
                      <div>
                        <strong>{{ robotTypes.length }} robot types</strong>
                        <span>横向浏览并比较，选择后仍可切换。</span>
                      </div>
                      <div class="choice-rail-actions">
                        <button type="button" aria-label="向左浏览机器人类型" title="上一组" @click="scrollRobotTypeRail(-1)">
                          <i class="el-icon-arrow-left"></i>
                        </button>
                        <button type="button" aria-label="向右浏览机器人类型" title="下一组" @click="scrollRobotTypeRail(1)">
                          <i class="el-icon-arrow-right"></i>
                        </button>
                      </div>
                    </div>
                    <div ref="robotTypeRail" class="choice-grid robot-type-rail">
                      <button
                        v-for="item in robotTypes"
                        :key="item.value"
                        type="button"
                        :class="['choice-card', { selected: taskForm.expectedRobotType === item.value }]"
                        :aria-pressed="taskForm.expectedRobotType === item.value"
                        @click.prevent="selectRobotType(item.value)"
                      >
                        <span>{{ item.short }}</span>
                        <strong>{{ item.label }}</strong>
                        <p>{{ item.text }}</p>
                        <small v-if="taskForm.expectedRobotType === item.value">已选择</small>
                      </button>
                    </div>
                  </el-form-item>

                  <div v-else-if="activeQuestion.key === 'go2Action'" class="single-field">
                    <el-form-item label="Go2 Action" prop="go2Action" class="choice-form-item">
                      <div class="choice-grid robot-choice-grid">
                        <button
                          v-for="item in go2ActionTypes"
                          :key="item.value"
                          type="button"
                          :class="['choice-card', { selected: taskForm.go2Action === item.value }]"
                          :aria-pressed="taskForm.go2Action === item.value"
                          @click.prevent="selectGo2Action(item.value)"
                        >
                          <span>GO2</span>
                          <strong>{{ item.label }}</strong>
                          <p>{{ item.text }}</p>
                          <small v-if="taskForm.go2Action === item.value">已选择</small>
                        </button>
                      </div>
                    </el-form-item>
                    <div v-if="taskForm.go2Action === 'PATROL_INSPECTION'" class="go2-preset-summary">
                      <strong>Preset route</strong>
                      <span>Forward 10 m -> rotate 360 deg -> forward 10 m</span>
                    </div>
                    <el-form-item v-if="['MOVE_FORWARD', 'MOVE_BACKWARD'].includes(taskForm.go2Action)" label="Move distance (m)" prop="go2ActionMeters">
                      <el-input-number
                        v-model="taskForm.go2ActionMeters"
                        :min="0.2"
                        :max="20"
                        :step="0.5"
                        controls-position="right"
                      />
                    </el-form-item>
                    <el-form-item v-if="taskForm.go2Action === 'TURN_AROUND'" label="Turn duration (s)" prop="go2ActionSeconds">
                      <el-input-number
                        v-model="taskForm.go2ActionSeconds"
                        :min="1"
                        :max="12"
                        :step="1"
                        controls-position="right"
                      />
                    </el-form-item>
                  </div>

                  <div v-else-if="activeQuestion.key === 'title'" class="single-field">
                    <el-form-item label="任务标题" prop="title">
                      <el-input
                        ref="titleInput"
                        v-model.trim="taskForm.title"
                        placeholder="例如：工业园夜间巡检"
                        @keyup.enter="nextStep"
                      ></el-input>
                    </el-form-item>
                    <el-form-item label="任务名称（可选）">
                      <el-input
                        v-model.trim="taskForm.taskName"
                        placeholder="不填则默认使用标题"
                        @keyup.enter="nextStep"
                      ></el-input>
                    </el-form-item>
                  </div>

                  <div v-else-if="activeQuestion.key === 'location'" class="single-field">
                    <div class="form-grid two-col" v-if="taskForm.serviceType === 'SHUTTLE'">
                      <el-form-item label="起点" prop="beginAddress">
                        <address-picker
                          v-model="taskForm.beginAddress"
                          :lat="taskForm.beginLat"
                          :lng="taskForm.beginLng"
                          placeholder="请在地图上选择起点"
                          dialog-title="选择起点"
                          search-placeholder="请输入起点关键词"
                          @change="applyLocation('begin', $event)"
                        />
                      </el-form-item>

                      <el-form-item label="终点" prop="endAddress">
                        <address-picker
                          v-model="taskForm.endAddress"
                          :lat="taskForm.endLat"
                          :lng="taskForm.endLng"
                          placeholder="请在地图上选择终点"
                          dialog-title="选择终点"
                          search-placeholder="请输入终点关键词"
                          @change="applyLocation('end', $event)"
                        />
                      </el-form-item>
                    </div>
                    <div v-if="taskForm.serviceType === 'SHUTTLE'" class="route-estimate-card">
                      <div>
                        <span>预估距离</span>
                        <strong>{{ routeDistanceText }}</strong>
                      </div>
                      <div>
                        <span>预计时间</span>
                        <strong>{{ routeDurationText }}</strong>
                      </div>
                      <p>{{ routeEstimateHint }}</p>
                    </div>

                    <el-form-item v-else label="服务地址" prop="serviceAddress">
                      <address-picker
                        v-model="taskForm.serviceAddress"
                        :lat="taskForm.serviceLat"
                        :lng="taskForm.serviceLng"
                        placeholder="请在地图上选择服务地址"
                        dialog-title="选择服务地址"
                        search-placeholder="请输入服务地址关键词"
                        @change="applyLocation('service', $event)"
                      />
                    </el-form-item>
                  </div>

                  <div v-else-if="activeQuestion.key === 'scheduleTime'" class="single-field">
                    <el-form-item label="预约时间" prop="scheduleTime">
                      <input
                        class="native-datetime-input full-width"
                        type="datetime-local"
                        :value="datetimeLocalValue(taskForm.scheduleTime)"
                        @input="setScheduleTime($event.target.value)"
                        @change="autoAdvanceWhenFilled('scheduleTime')"
                      />
                    </el-form-item>
                  </div>

                  <div v-else-if="activeQuestion.key === 'contactPhone'" class="single-field">
                    <el-form-item label="联系电话" prop="contactPhone">
                      <el-input
                        v-model.trim="taskForm.contactPhone"
                        placeholder="请输入联系电话"
                        @keyup.enter="nextStep"
                      ></el-input>
                    </el-form-item>
                  </div>

                  <div v-else-if="activeQuestion.key === 'message'" class="single-field">
                    <el-form-item label="任务描述" prop="message">
                      <el-input
                        v-model.trim="taskForm.message"
                        type="textarea"
                        :rows="6"
                        placeholder="请描述任务场景、需求内容和时间要求"
                      />
                    </el-form-item>
                  </div>

                  <div v-else-if="activeQuestion.key === 'subcontract'" class="subcontract-policy">
                      <button
                        type="button"
                        :disabled="Boolean(editingId)"
                        :class="{ selected: taskForm.allowSubcontract === 0 }"
                      @click="taskForm.allowSubcontract = 0"
                    >
                      <span>整单履约</span>
                      <strong>禁止分包</strong>
                      <p>接单机器人独立完成全部任务，执行中不能转交。</p>
                    </button>
                      <button
                        type="button"
                        :disabled="Boolean(editingId)"
                        :class="{ selected: taskForm.allowSubcontract === 1 }"
                      @click="taskForm.allowSubcontract = 1"
                    >
                      <span>两段协作</span>
                      <strong>允许分包</strong>
                      <p>接单时必须锁定两台机器人、交接点和分账比例。</p>
                    </button>
                  </div>

                  <div v-else-if="activeQuestion.key === 'payment'" class="payment-panel">
                    <div class="payment-card">
                      <div>
                        <span>平台补助</span>
                        <strong>{{ estimatedRobocoin }} RoboCoin</strong>
                        <p>{{ estimateSummary }}</p>
                      </div>
                      <div v-if="taskForm.serviceType === 'SHUTTLE'" class="route-charge-list">
                        <span>距离 {{ routeDistanceText }}</span>
                        <span>时间 {{ routeDurationText }}</span>
                      </div>
                    </div>
                    <div class="payment-rules">
                      <div>
                        <strong>当前阶段</strong>
                        <p>当前阶段由平台提供任务补助，用户无需先行支付 RoboCoin。</p>
                      </div>
                      <div>
                        <strong>后续钱包</strong>
                        <p>后续接入用户钱包后，将从用户钱包中冻结对应 RoboCoin。</p>
                      </div>
                      <div>
                        <strong>任务完成</strong>
                        <p>任务完成后按实际服务结果结算，当前先仅展示平台补助额度。</p>
                      </div>
                    </div>
                  </div>

                  <div v-else class="final-review">
                    <div class="review-head">
                      <div>
                        <span>Final Check</span>
                        <h4>请最后确认任务清单</h4>
                      </div>
                      <p>发现信息不对，直接在对应清单项里修改；确认无误后再发布。</p>
                      <div class="review-meter">
                        <strong>{{ completedReviewItems }}/{{ reviewItems.length }}</strong>
                        <em>已完成</em>
                      </div>
                    </div>
                    <div class="review-list">
                      <div
                        v-for="item in reviewItems"
                        :key="item.key"
                        :class="['review-item', { editing: reviewEditingKey === item.key, incomplete: !item.ready }]"
                      >
                        <div class="review-main">
                          <div class="review-index">{{ item.order }}</div>
                          <div class="review-copy">
                            <span>{{ item.label }}</span>
                            <strong>{{ item.value }}</strong>
                          </div>
                          <button type="button" @click="toggleReviewEdit(item.key)">
                            {{ reviewEditingKey === item.key ? '收起' : '修改' }}
                          </button>
                        </div>
                        <transition name="review-editor">
                          <div v-if="reviewEditingKey === item.key" class="review-editor">
                            <div v-if="item.key === 'serviceType'" class="review-choice-row">
                              <button
                                v-for="option in serviceTypes"
                                :key="option.value"
                                type="button"
                                :class="['mini-choice', { selected: taskForm.serviceType === option.value }]"
                                @click="setServiceType(option.value)"
                              >
                                <span>{{ option.badge }}</span>
                                <strong>{{ option.label }}</strong>
                              </button>
                            </div>

                            <div v-else-if="item.key === 'expectedRobotType'" class="review-choice-row robot-review-choices">
                              <button
                                v-for="option in robotTypes"
                                :key="option.value"
                                type="button"
                                :class="['mini-choice', { selected: taskForm.expectedRobotType === option.value }]"
                                @click="setRobotType(option.value)"
                              >
                                <span>{{ option.short }}</span>
                                <strong>{{ option.label }}</strong>
                              </button>
                            </div>

                            <div v-else-if="item.key === 'title'" class="review-field-grid">
                              <el-form-item label="任务标题" prop="title">
                                <el-input v-model.trim="taskForm.title" placeholder="例如：工业园夜间巡检" />
                              </el-form-item>
                              <el-form-item label="任务名称（可选）">
                                <el-input v-model.trim="taskForm.taskName" placeholder="不填则默认使用标题" />
                              </el-form-item>
                            </div>

                            <div v-else-if="item.key === 'location'" class="review-field-grid">
                              <div class="form-grid two-col" v-if="taskForm.serviceType === 'SHUTTLE'">
                                <el-form-item label="起点" prop="beginAddress">
                                  <address-picker
                                    v-model="taskForm.beginAddress"
                                    :lat="taskForm.beginLat"
                                    :lng="taskForm.beginLng"
                                    placeholder="请在地图上选择起点"
                                    dialog-title="选择起点"
                                    search-placeholder="请输入起点关键词"
                                    @change="applyLocation('begin', $event)"
                                  />
                                </el-form-item>
                                <el-form-item label="终点" prop="endAddress">
                                  <address-picker
                                    v-model="taskForm.endAddress"
                                    :lat="taskForm.endLat"
                                    :lng="taskForm.endLng"
                                    placeholder="请在地图上选择终点"
                                    dialog-title="选择终点"
                                    search-placeholder="请输入终点关键词"
                                    @change="applyLocation('end', $event)"
                                  />
                                </el-form-item>
                              </div>
                              <div v-if="taskForm.serviceType === 'SHUTTLE'" class="route-estimate-card review-route-card">
                                <div>
                                  <span>预估距离</span>
                                  <strong>{{ routeDistanceText }}</strong>
                                </div>
                                <div>
                                  <span>预计时间</span>
                                  <strong>{{ routeDurationText }}</strong>
                                </div>
                                <p>{{ routeEstimateHint }}</p>
                              </div>

                              <el-form-item v-else label="服务地址" prop="serviceAddress">
                                <address-picker
                                  v-model="taskForm.serviceAddress"
                                  :lat="taskForm.serviceLat"
                                  :lng="taskForm.serviceLng"
                                  placeholder="请在地图上选择服务地址"
                                  dialog-title="选择服务地址"
                                  search-placeholder="请输入服务地址关键词"
                                  @change="applyLocation('service', $event)"
                                />
                              </el-form-item>
                            </div>

                            <div v-else-if="item.key === 'scheduleTime'" class="review-field-grid">
                              <el-form-item label="预约时间" prop="scheduleTime">
                                <input
                                  class="native-datetime-input full-width"
                                  type="datetime-local"
                                  :value="datetimeLocalValue(taskForm.scheduleTime)"
                                  @input="setScheduleTime($event.target.value)"
                                />
                              </el-form-item>
                            </div>

                            <div v-else-if="item.key === 'contactPhone'" class="review-field-grid">
                              <el-form-item label="联系电话" prop="contactPhone">
                                <el-input v-model.trim="taskForm.contactPhone" placeholder="请输入联系电话" />
                              </el-form-item>
                            </div>

                            <div v-else-if="item.key === 'message'" class="review-field-grid">
                              <el-form-item label="任务描述" prop="message">
                                <el-input
                                  v-model.trim="taskForm.message"
                                  type="textarea"
                                  :rows="4"
                                  placeholder="请描述任务场景、需求内容和时间要求"
                                />
                              </el-form-item>
                            </div>

                            <div v-else-if="item.key === 'payment'" class="review-field-grid">
                              <div class="payment-review-box">
                                <strong>{{ estimatedRobocoin }} RoboCoin</strong>
                                <p>{{ paymentStatusText }}</p>
                                <p v-if="taskForm.serviceType === 'SHUTTLE'">接驳预估：{{ routeDistanceText }}，{{ routeDurationText }}。</p>
                              </div>
                            </div>

                            <div v-else-if="item.key === 'subcontract'" class="review-choice-row">
                              <button
                                type="button"
                                :disabled="Boolean(editingId)"
                                :class="['mini-choice', { selected: taskForm.allowSubcontract === 0 }]"
                                @click="taskForm.allowSubcontract = 0"
                              >
                                <span>Direct</span>
                                <strong>禁止分包</strong>
                              </button>
                              <button
                                type="button"
                                :disabled="Boolean(editingId)"
                                :class="['mini-choice', { selected: taskForm.allowSubcontract === 1 }]"
                                @click="taskForm.allowSubcontract = 1"
                              >
                                <span>2 Stages</span>
                                <strong>允许两段协作</strong>
                              </button>
                            </div>

                            <div class="review-editor-actions">
                              <button class="review-done-btn" type="button" @click="finishReviewEdit(item)">完成修改</button>
                              <button class="review-step-btn" type="button" @click="jumpToStepFromReview(item.step)">回到这一步</button>
                            </div>
                          </div>
                        </transition>
                      </div>
                    </div>
                  </div>
                </section>
              </transition>
            </el-form>

            <section class="mobile-task-summary">
              <div class="summary-head">
                <div>
                  <p class="eyebrow">Task Summary</p>
                  <h2>发布前摘要</h2>
                </div>
                <span>{{ completedSummaryItems }}/{{ sidebarSummaryItems.length }}</span>
              </div>
              <div class="summary-list">
                <div v-for="item in sidebarSummaryItems" :key="`mobile-${item.key}`" class="summary-item">
                  <i :class="{ ready: item.ready }"></i>
                  <div>
                    <span>{{ item.label }}</span>
                    <strong>{{ item.value }}</strong>
                  </div>
                </div>
              </div>
            </section>

            <div class="wizard-actions">
              <button class="ghost-btn" type="button" @click="prevStep" :disabled="currentStep === 1">上一步</button>
              <button v-if="!isReviewStep" class="primary-btn" type="button" @click="nextStep">下一步</button>
              <button v-else class="primary-btn" type="button" @click="submitTask">{{ editingId ? '确认保存' : '确认发布' }}</button>
            </div>
          </section>
        </section>

        <section class="list-card reveal-up delay-3">
          <div class="card-title-row">
            <div>
              <p class="eyebrow">我的任务</p>
              <h3>已发布任务</h3>
            </div>
            <button class="ghost-btn" @click="loadMyTasks">刷新</button>
          </div>

          <el-table :data="myTasks" stripe class="task-table" empty-text="当前账号暂无已发布任务，请确认登录账号后刷新">
            <el-table-column prop="title" label="标题" min-width="180" />
            <el-table-column prop="expectedRobotType" label="机器人类型" min-width="140" />
            <el-table-column prop="serviceType" label="服务类型" min-width="120">
              <template #default="scope">
                {{ serviceTypeLabel(scope.row && scope.row.serviceType) }}
              </template>
            </el-table-column>
            <el-table-column prop="estimatedRobocoin" label="平台补助" min-width="130">
              <template #default="scope">
                <strong>{{ formatCoin(scope.row && scope.row.estimatedRobocoin) }}</strong>
                <span class="coin-unit">RoboCoin</span>
              </template>
            </el-table-column>
            <el-table-column prop="verificationNote" label="审核说明" min-width="180" />
            <el-table-column prop="publishStatus" label="发布状态" min-width="140">
              <template #default="scope">
                <el-tag :type="publishStatusTag(scope.row)">{{ publishStatusLabel(scope.row) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="DeRAS派单进度" min-width="260">
              <template #default="scope">
                <div class="dispatch-progress-cell">
                  <strong>{{ dispatchStatusText(scope.row) }}</strong>
                  <span>{{ dispatchProgressDesc(scope.row) }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="createTime" label="创建时间" min-width="180" />
            <el-table-column label="操作" width="190" fixed="right">
              <template #default="scope">
                <template v-if="scope.row">
                  <el-button v-if="canDeleteTask(scope.row)" type="text" class="danger-text" @click="deleteTask(scope.row)">Delete</el-button>
                  <el-button type="text" @click="startEdit(scope.row)" :disabled="!canEditTask(scope.row)">编辑</el-button>
                  <el-button type="text" class="danger-text" @click="cancelTask(scope.row)" :disabled="!canCancelTask(scope.row)">取消发布</el-button>
                </template>
              </template>
            </el-table-column>
          </el-table>
        </section>
      </main>
    </div>

    <el-dialog
      :visible.sync="submitDialogVisible"
      width="360px"
      custom-class="process-dialog"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      append-to-body
    >
      <div class="process-content">
        <i :class="submitDialogDone ? 'el-icon-circle-check process-success' : 'el-icon-loading process-loading'"></i>
        <h3>{{ submitDialogTitle }}</h3>
        <p>{{ submitDialogText }}</p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import AddressPicker from '../components/AddressPicker.vue'
import { cancelPublishedTask, deletePublishedTask, getMyPublishedTasks, getTaskEstimate, publishTask, updatePublishedTask } from '../api/task-publish'
import { getApiErrorMessage } from '../api/http'
import { getAuthUser } from '../utils/auth'
import { isValidInternationalPhone, normalizePhone } from '../utils/phone'

function validateContactPhone(rule, value, callback) {
  if (!normalizePhone(value)) return callback(new Error('请输入联系电话'))
  if (!isValidInternationalPhone(value)) return callback(new Error('请输入 7 至 15 位有效电话号码，可包含国际区号'))
  callback()
}

function validateFutureSchedule(rule, value, callback) {
  if (!value) return callback(new Error('请选择预约时间'))
  const timestamp = new Date(String(value).replace(' ', 'T')).getTime()
  if (!Number.isFinite(timestamp)) return callback(new Error('预约时间格式不正确'))
  if (timestamp <= Date.now()) return callback(new Error('预约时间必须晚于当前时间'))
  callback()
}

export default {
  name: 'TaskPublishPage',
  components: {
    AddressPicker
  },
  data() {
    const requirePickedLocation = (mode, message) => (rule, value, callback) => {
      if (this.taskForm.serviceType !== mode) {
        callback()
        return
      }

      const fieldMap = {
        serviceAddress: ['serviceAddress', 'serviceLat', 'serviceLng'],
        beginAddress: ['beginAddress', 'beginLat', 'beginLng'],
        endAddress: ['endAddress', 'endLat', 'endLng']
      }
      const [addressKey, latKey, lngKey] = fieldMap[rule.field] || []
      const hasAddress = addressKey && !!this.taskForm[addressKey]
      const hasCoordinates = latKey && lngKey &&
        this.taskForm[latKey] !== '' &&
        this.taskForm[lngKey] !== '' &&
        this.taskForm[latKey] !== null &&
        this.taskForm[lngKey] !== null

      if (!hasAddress || !hasCoordinates) {
        callback(new Error(message))
        return
      }

      callback()
    }

    return {
      currentUser: getAuthUser(),
      editingId: null,
      myTasks: [],
      currentStep: 1,
      submitDialogVisible: false,
      submitDialogDone: false,
      submitDialogTitle: '',
      submitDialogText: '',
      maxVisitedStep: 1,
      reviewEditingKey: '',
      estimateLoading: false,
      estimateInfo: {
        estimatedRobocoin: 3,
        estimatedServiceMinutes: 120,
        estimateSource: 'DEFAULT',
        robocoinHourlyRate: 1.5,
        routeDistanceMeters: null,
        robocoinDistanceRate: 0,
        message: '暂无历史完成任务，已按默认服务时长预估。'
      },
      robotTypes: [
        { value: 'CLEANING', label: '清洁机器人', short: '扫', text: '适合扫地、保洁、园区清扫。' },
        { value: 'DELIVERY', label: '配送机器人', short: '配', text: '适合物品配送、楼宇转运。' },
        { value: 'SHUTTLE', label: '接驳机器人', short: '驳', text: '适合人员或货物短途接驳。' },
        { value: 'DRONE', label: '无人机', short: '飞', text: '适合巡航、空中查看、轻量配送。' },
        { value: 'INSPECTION', label: '巡检机器人', short: '巡', text: '适合巡逻、检查、异常发现。' },
        { value: 'GO2', label: 'Unitree Go2', short: 'Go2', text: 'Stand, heart gesture, dance, turn, and move.' }
      ],
      serviceTypes: [
        { value: 'HOME_SERVICE', label: '上门服务', badge: '到点', text: '机器人到一个固定地点完成服务。' },
        { value: 'SHUTTLE', label: '接驳出行', badge: '路线', text: '机器人从起点到终点执行接驳。' }
      ],
      go2ActionTypes: [
        { value: 'STAND_UP', label: 'Stand Up', text: 'Switch Go2 to a stable standing posture.' },
        { value: 'HEART', label: 'Heart Gesture', text: 'Run the preset heart gesture.' },
        { value: 'PATROL_INSPECTION', label: 'Inspection Patrol', text: 'Forward 10 m, rotate 360 degrees, then forward 10 m.' },
        { value: 'DANCE_1', label: 'Dance 1', text: 'Run the first preset dance.' },
        { value: 'DANCE_2', label: 'Dance 2', text: 'Run the second preset dance.' },
        { value: 'MOVE_FORWARD', label: 'Move Forward', text: 'Move forward by the configured distance.' },
        { value: 'MOVE_BACKWARD', label: 'Move Backward', text: 'Move backward by the configured distance.' },
        { value: 'TURN_AROUND', label: 'Turn Around', text: 'About 180 degrees in 4 seconds, about 360 degrees in 8 seconds.' },
        { value: 'STOP', label: 'Stop', text: 'Stop the current motion command.' }
      ],
      taskForm: this.createEmptyForm(),
      taskRules: {
        serviceType: [{ required: true, message: '请选择服务类型', trigger: 'change' }],
        expectedRobotType: [{ required: true, message: '请选择机器人类型', trigger: 'change' }],
        title: [{ required: true, message: '请输入任务标题', trigger: 'blur' }],
        message: [{ required: true, message: '请输入任务描述', trigger: 'blur' }],
        contactPhone: [{ validator: validateContactPhone, trigger: 'blur' }],
        scheduleTime: [{ validator: validateFutureSchedule, trigger: 'change' }],
        serviceAddress: [{ validator: requirePickedLocation('HOME_SERVICE', '请在地图上确认服务地址'), trigger: 'change' }],
        beginAddress: [{ validator: requirePickedLocation('SHUTTLE', '请在地图上确认起点'), trigger: 'change' }],
        endAddress: [{ validator: requirePickedLocation('SHUTTLE', '请在地图上确认终点'), trigger: 'change' }]
      }
    }
  },
  computed: {
    currentUserName() {
      if (!this.currentUser) {
        return '未登录用户'
      }
      return this.currentUser.username || this.currentUser.email || this.currentUser.phone || `用户#${this.currentUser.id}`
    },
    guidedSteps() {
      const steps = [
        { key: 'serviceType', short: 'Service', title: 'Choose service type', help: '' },
        { key: 'expectedRobotType', short: 'Robot', title: 'Choose robot type', help: '可横向浏览并比较机器人类型，选中后点击下一步继续。' }
      ]
      if (this.taskForm.expectedRobotType === 'GO2') {
        steps.push({ key: 'go2Action', short: 'Go2', title: 'Choose Go2 action', help: '' })
      }
      steps.push(
        { key: 'title', short: 'Title', title: 'Task title', help: '' },
        { key: 'location', short: 'Location', title: this.taskForm.serviceType === 'SHUTTLE' ? 'Choose start and end points' : 'Choose service address', help: '' },
        { key: 'scheduleTime', short: 'Time', title: 'Choose schedule time', help: '' },
        { key: 'contactPhone', short: 'Phone', title: 'Enter contact phone', help: '' },
        { key: 'message', short: 'Details', title: 'Add task details', help: '' },
        { key: 'subcontract', short: 'Split', title: '是否允许任务分包', help: '' },
        { key: 'payment', short: 'Subsidy', title: 'Platform RoboCoin subsidy', help: '' },
        { key: 'review', short: 'Review', title: 'Review task details', help: '' }
      )
      return steps
    },
    activeQuestion() {
      return this.guidedSteps[this.currentStep - 1] || this.guidedSteps[0]
    },
    paddedStep() {
      return String(this.currentStep).padStart(2, '0')
    },
    isReviewStep() {
      return this.activeQuestion.key === 'review'
    },
    estimatedRobocoin() {
      return this.formatCoin(this.estimateInfo.estimatedRobocoin)
    },
    routeDistanceMeters() {
      if (this.taskForm.serviceType !== 'SHUTTLE') {
        return null
      }
      const liveDistance = this.calculateRouteDistanceMeters()
      if (Number.isFinite(liveDistance) && liveDistance > 0) {
        return liveDistance
      }
      const estimated = Number(this.estimateInfo.routeDistanceMeters)
      return Number.isFinite(estimated) && estimated > 0 ? estimated : null
    },
    routeDistanceText() {
      const meters = this.routeDistanceMeters
      if (!Number.isFinite(meters) || meters <= 0) {
        return '选择起点和终点后自动计算'
      }
      if (meters < 1000) {
        return `${Math.round(meters)} 米`
      }
      return `${(meters / 1000).toFixed(2)} 公里`
    },
    routeDurationText() {
      const minutes = Number(this.estimateInfo.estimatedServiceMinutes || this.estimateRouteMinutes())
      if (!Number.isFinite(minutes) || minutes <= 0) {
        return '等待路线距离'
      }
      return this.formatDuration(minutes)
    },
    routeEstimateHint() {
      if (this.taskForm.serviceType !== 'SHUTTLE') {
        return ''
      }
      if (!Number.isFinite(this.routeDistanceMeters) || this.routeDistanceMeters <= 0) {
        return '起点和终点都在地图上确认后，系统会立即计算接驳距离、预计时间和平台补助额度。'
      }
      return `当前按约 ${this.routeDistanceText}、${this.routeDurationText} 预估；距离单价 ${this.formatCoin(this.estimateInfo.robocoinDistanceRate)} RoboCoin/公里，时间单价 ${this.formatCoin(this.estimateInfo.robocoinHourlyRate)} RoboCoin/小时。`
    },
    estimateSummary() {
      const minutes = Number(this.estimateInfo.estimatedServiceMinutes || 0)
      if (this.taskForm.serviceType === 'HOME_SERVICE') {
        const sourceText = this.estimateInfo.estimateSource === 'HISTORY'
          ? '已根据历史完成任务平均时长预估'
          : '暂无历史完成任务，先按默认时长预估'
        return `${sourceText}：约 ${this.formatDuration(minutes)}，当前阶段由平台按 ${this.formatCoin(this.estimateInfo.robocoinHourlyRate)} RoboCoin/小时提供任务补助。`
      }
      return this.estimateInfo.message || this.routeEstimateHint
    },
    paymentStatusText() {
      return '当前阶段由平台提供任务补助；后续接入用户钱包后，将从用户钱包中冻结对应 RoboCoin，任务完成后按实际服务结果结算。'
    },
    sidebarSummaryItems() {
      const summaryKeys = ['serviceType', 'expectedRobotType', 'location', 'scheduleTime']
      return summaryKeys
        .map(key => this.reviewItems.find(item => item.key === key))
        .filter(Boolean)
    },
    completedSummaryItems() {
      return this.sidebarSummaryItems.filter(item => item.ready).length
    },
    completedReviewItems() {
      return this.reviewItems.filter(item => item.ready).length
    },
    reviewItems() {
      const address = this.taskForm.serviceType === 'SHUTTLE'
        ? `${this.taskForm.beginAddress || '未选择起点'} → ${this.taskForm.endAddress || '未选择终点'} · ${this.routeDistanceText}`
        : (this.taskForm.serviceAddress || '未选择服务地址')
      return [
        { key: 'serviceType', order: '01', label: '服务类型', value: this.serviceTypeLabel(this.taskForm.serviceType), step: 1, ready: !!this.taskForm.serviceType },
        { key: 'expectedRobotType', order: '02', label: 'Robot Type', value: this.robotTypeLabel(this.taskForm.expectedRobotType), step: this.stepNumber('expectedRobotType'), ready: !!this.taskForm.expectedRobotType },
        ...(this.taskForm.expectedRobotType === 'GO2' ? [{ key: 'go2Action', order: '02A', label: 'Go2 Action', value: this.go2ActionText(), step: this.stepNumber('go2Action'), ready: !!this.taskForm.go2Action }] : []),
        { key: 'title',  order: '03', label: '任务标题', value: this.taskForm.title || '未填写', step: this.stepNumber('title'), ready: !!this.taskForm.title },
        { key: 'location', order: '04', label: '服务位置', value: address, step: this.stepNumber('location'), ready: this.isLocationReady() },
        { key: 'scheduleTime', order: '05', label: '预约时间', value: this.taskForm.scheduleTime || '未选择', step: this.stepNumber('scheduleTime'), ready: !!this.taskForm.scheduleTime },
        { key: 'contactPhone', order: '06', label: '联系电话', value: this.taskForm.contactPhone || '未填写', step: this.stepNumber('contactPhone'), ready: !!this.taskForm.contactPhone },
        { key: 'message', order: '07', label: '任务描述', value: this.taskForm.message || '未填写', step: this.stepNumber('message'), ready: !!this.taskForm.message },
        { key: 'subcontract', order: '08', label: '任务分包属性', value: this.taskForm.allowSubcontract === 1 ? '允许接单时确定两段协作' : '禁止分包，整单履约', step: this.stepNumber('subcontract'), ready: true },
        { key: 'payment', order: '09', label: '平台补助', value: `${this.estimatedRobocoin} RoboCoin`, step: this.stepNumber('payment'), ready: true }
      ]
    }
  },
  created() {
    this.refreshEstimate()
    this.loadMyTasks()
  },
  methods: {
    createEmptyForm() {
      return {
        taskName: '',
        title: '',
        message: '',
        typeId: 1,
        serviceType: '',
        expectedRobotType: '',
        allowSubcontract: 0,
        go2Action: '',
        go2ActionMeters: 1,
        go2ActionSeconds: null,
        serviceAddress: '',
        serviceLat: '',
        serviceLng: '',
        contactPhone: '',
        scheduleTime: '',
        beginAddress: '',
        beginLat: '',
        beginLng: '',
        endAddress: '',
        endLat: '',
        endLng: '',
        estimatedRobocoin: 3,
        estimatedServiceMinutes: 120,
        estimateSource: 'DEFAULT',
        robocoinHourlyRate: 1.5,
        routeDistanceMeters: null,
        robocoinDistanceRate: 0,
        prepayStatus: 'PAID',
        paymentMethod: 'PLATFORM_SUBSIDY'
      }
    },
    goHome() {
      this.$router.push({ name: 'home' }).catch(error => {
        console.error('[TaskPublish] failed to return home:', error)
        window.location.assign('/#/home')
      })
    },
    resetForm() {
      this.editingId = null
      this.currentStep = 1
      this.maxVisitedStep = 1
      this.reviewEditingKey = ''
      this.taskForm = this.createEmptyForm()
      this.refreshEstimate()
      this.$nextTick(() => {
        if (this.$refs.taskFormRef) {
          this.$refs.taskFormRef.clearValidate()
        }
      })
    },
    goToStep(step) {
      if (step <= this.maxVisitedStep || step < this.currentStep) {
        this.reviewEditingKey = ''
        this.currentStep = step
      }
    },
    validateFields(fields) {
      const targetFields = fields || []
      if (!targetFields.length) return Promise.resolve(true)
      const invalid = targetFields.some(field => {
        const value = this.taskForm[field]
        if (field === 'serviceType') return !this.taskForm.serviceType
        if (field === 'expectedRobotType') return !this.taskForm.expectedRobotType
        if (field === 'go2Action') return this.taskForm.expectedRobotType === 'GO2' && !this.taskForm.go2Action
        if (field === 'title') return !String(this.taskForm.title || '').trim()
        if (field === 'message') return !String(this.taskForm.message || '').trim()
        if (field === 'contactPhone') return !isValidInternationalPhone(this.taskForm.contactPhone)
        if (field === 'scheduleTime') return !this.taskForm.scheduleTime
        if (field === 'serviceAddress' || field === 'beginAddress' || field === 'endAddress') return !this.isLocationReady()
        if (field === 'prepayStatus') return false
        return value === undefined || value === null || String(value).trim() === ''
      })
      return Promise.resolve(!invalid)
    },
    stepFields(step) {
      const key = this.guidedSteps[step - 1] && this.guidedSteps[step - 1].key
      if (key === 'serviceType') return ['serviceType']
      if (key === 'expectedRobotType') return ['expectedRobotType']
      if (key === 'go2Action') return ['go2Action']
      if (key === 'title') return ['title']
      if (key === 'location') {
        const locationFields = this.taskForm.serviceType === 'SHUTTLE'
          ? ['beginAddress', 'endAddress']
          : ['serviceAddress']
        return locationFields
      }
      if (key === 'scheduleTime') return ['scheduleTime']
      if (key === 'contactPhone') return ['contactPhone']
      if (key === 'message') return ['message']
      if (key === 'subcontract') return []
      if (key === 'payment') return []
      return []
    },
    validationMessage(fields) {
      for (const field of fields) {
        if (field === 'serviceType' && !this.taskForm.serviceType) return 'Please choose a service type first.'
        if (field === 'expectedRobotType' && !this.taskForm.expectedRobotType) return 'Please choose a robot type first.'
        if (field === 'go2Action' && this.taskForm.expectedRobotType === 'GO2' && !this.taskForm.go2Action) return 'Please choose a Go2 action first.'
        if (field === 'title' && !this.taskForm.title) return 'Please enter the task title.'
        if (field === 'message' && !this.taskForm.message) return 'Please enter the task details.'
        if (field === 'contactPhone' && !this.taskForm.contactPhone) return 'Please enter the contact phone.'
        if (field === 'contactPhone' && !isValidInternationalPhone(this.taskForm.contactPhone)) return 'Please enter a valid phone number with 7 to 15 digits.'
        if (field === 'scheduleTime' && !this.taskForm.scheduleTime) return 'Please choose the schedule time.'
        if (['serviceAddress', 'beginAddress', 'endAddress'].includes(field) && !this.isLocationReady()) {
          return this.taskForm.serviceType === 'SHUTTLE'
            ? 'Please choose both start and end points on the map.'
            : 'Please choose the service address on the map.'
        }
      }
      return 'Please complete this step before continuing.'
    },
    async nextStep() {
      if (this.isReviewStep) return
      const valid = await this.validateFields(this.stepFields(this.currentStep))
      if (!valid) {
        this.$message.warning(this.validationMessage(this.stepFields(this.currentStep)))
        return
      }
      const next = Math.min(this.currentStep + 1, this.guidedSteps.length)
      this.currentStep = next
      this.maxVisitedStep = Math.max(this.maxVisitedStep, next)
      this.reviewEditingKey = ''
    },
    prevStep() {
      this.reviewEditingKey = ''
      this.currentStep = Math.max(this.currentStep - 1, 1)
    },
    setServiceType(value) {
      this.taskForm.serviceType = value
      this.taskForm.prepayStatus = 'PAID'
      this.taskForm.paymentMethod = 'PLATFORM_SUBSIDY'
      if (value === 'HOME_SERVICE') {
        this.taskForm.beginAddress = ''
        this.taskForm.beginLat = ''
        this.taskForm.beginLng = ''
        this.taskForm.endAddress = ''
        this.taskForm.endLat = ''
        this.taskForm.endLng = ''
        this.taskForm.routeDistanceMeters = null
        this.taskForm.robocoinDistanceRate = 0
      } else {
        this.taskForm.serviceAddress = ''
        this.taskForm.serviceLat = ''
        this.taskForm.serviceLng = ''
        this.taskForm.estimatedServiceMinutes = this.estimateRouteMinutes()
      }
      if (this.$refs.taskFormRef) {
        this.$refs.taskFormRef.clearValidate(['serviceType', 'serviceAddress', 'beginAddress', 'endAddress', 'prepayStatus'])
      }
      this.refreshEstimate()
    },
    selectServiceType(value) {
      this.setServiceType(value)
    },
    selectRobotType(value) {
      this.setRobotType(value)
    },
    scrollRobotTypeRail(direction) {
      const rail = this.$refs.robotTypeRail
      if (!rail) return
      rail.scrollBy({
        left: direction * Math.max(240, rail.clientWidth * 0.72),
        behavior: 'smooth'
      })
    },
    setRobotType(value) {
      this.taskForm.expectedRobotType = value
      if (value !== 'GO2') {
        this.taskForm.go2Action = ''
        this.taskForm.go2ActionMeters = 1
        this.taskForm.go2ActionSeconds = null
      }
      this.taskForm.prepayStatus = 'PAID'
      this.taskForm.paymentMethod = 'PLATFORM_SUBSIDY'
      if (this.$refs.taskFormRef) {
        this.$refs.taskFormRef.clearValidate(['expectedRobotType', 'prepayStatus'])
      }
    },
    selectGo2Action(value) {
      this.taskForm.go2Action = value
      this.taskForm.go2ActionMeters = ['MOVE_FORWARD', 'MOVE_BACKWARD', 'PATROL_INSPECTION'].includes(value)
        ? (value === 'PATROL_INSPECTION' ? 10 : (Number(this.taskForm.go2ActionMeters) > 0 ? this.taskForm.go2ActionMeters : 1))
        : null
      this.taskForm.go2ActionSeconds = ['TURN_AROUND', 'PATROL_INSPECTION'].includes(value)
        ? (value === 'PATROL_INSPECTION' ? 9 : (Number(this.taskForm.go2ActionSeconds) > 0 ? this.taskForm.go2ActionSeconds : 4))
        : null
      if (this.$refs.taskFormRef) {
        this.$refs.taskFormRef.clearValidate('go2Action')
      }
    },
    datetimeLocalValue(value) {
      if (!value) return ''
      return String(value).replace(' ', 'T').slice(0, 16)
    },
    setScheduleTime(value) {
      this.taskForm.scheduleTime = value ? String(value).replace('T', ' ') + ':00' : ''
    },    autoAdvanceWhenFilled(field) {
      if (this.taskForm[field]) {
        this.$nextTick(() => this.nextStep())
      }
    },
    coordinateValue(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : null
    },
    calculateRouteDistanceMeters(distanceOverride) {
      if (Number.isFinite(distanceOverride)) {
        return distanceOverride
      }
      const beginLat = this.coordinateValue(this.taskForm.beginLat)
      const beginLng = this.coordinateValue(this.taskForm.beginLng)
      const endLat = this.coordinateValue(this.taskForm.endLat)
      const endLng = this.coordinateValue(this.taskForm.endLng)
      if ([beginLat, beginLng, endLat, endLng].some(value => value === null)) {
        return null
      }
      const toRad = value => value * Math.PI / 180
      const earthRadius = 6371000
      const dLat = toRad(endLat - beginLat)
      const dLng = toRad(endLng - beginLng)
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(beginLat)) * Math.cos(toRad(endLat)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2)
      return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    },
    estimateRouteMinutes(distanceOverride) {
      const meters = this.calculateRouteDistanceMeters(distanceOverride)
      if (!Number.isFinite(meters) || meters <= 0) {
        return 45
      }
      const metersPerMinute = 12 * 1000 / 60
      return Math.max(10, Math.ceil(meters / metersPerMinute) + 8)
    },
    isLocationReady() {
      if (this.taskForm.serviceType === 'SHUTTLE') {
        return !!(
          this.taskForm.beginAddress &&
          this.taskForm.endAddress &&
          this.taskForm.beginLat !== '' &&
          this.taskForm.beginLng !== '' &&
          this.taskForm.endLat !== '' &&
          this.taskForm.endLng !== ''
        )
      }
      if (this.taskForm.serviceType === 'HOME_SERVICE') {
        return !!(this.taskForm.serviceAddress && this.taskForm.serviceLat !== '' && this.taskForm.serviceLng !== '')
      }
      return false
    },
    toggleReviewEdit(key) {
      this.reviewEditingKey = this.reviewEditingKey === key ? '' : key
    },
    jumpToStepFromReview(step) {
      this.reviewEditingKey = ''
      this.goToStep(step)
    },
    async finishReviewEdit(item) {
      const valid = await this.validateFields(this.stepFields(item.step))
      if (!valid) {
        this.$message.warning('这一项还没填写完整')
        return
      }
      this.reviewEditingKey = ''
    },
    applyLocation(prefix, payload) {
      const fieldMap = {
        service: ['serviceAddress', 'serviceLat', 'serviceLng'],
        begin: ['beginAddress', 'beginLat', 'beginLng'],
        end: ['endAddress', 'endLat', 'endLng']
      }
      const [addressKey, latKey, lngKey] = fieldMap[prefix]
      this.taskForm[addressKey] = payload.address
      this.taskForm[latKey] = payload.lat
      this.taskForm[lngKey] = payload.lng
      this.taskForm.prepayStatus = 'PAID'
      this.taskForm.paymentMethod = 'PLATFORM_SUBSIDY'
      if (this.taskForm.serviceType === 'SHUTTLE') {
        const distance = this.calculateRouteDistanceMeters()
        this.taskForm.routeDistanceMeters = Number.isFinite(distance) && distance > 0 ? Number(distance.toFixed(2)) : null
        this.taskForm.estimatedServiceMinutes = this.estimateRouteMinutes(distance)
      }
      if (this.$refs.taskFormRef) {
        this.$refs.taskFormRef.clearValidate([addressKey, 'prepayStatus'])
      }
      this.refreshEstimate()
      const locationComplete = this.isLocationReady()
      if (this.activeQuestion.key === 'location' && locationComplete) {
        window.setTimeout(() => this.nextStep(), 350)
      }
    },
    async refreshEstimate() {
      this.estimateLoading = true
      try {
        const routeDistance = this.taskForm.serviceType === 'SHUTTLE' ? this.calculateRouteDistanceMeters() : null
        const routeMinutes = this.taskForm.serviceType === 'SHUTTLE' ? this.estimateRouteMinutes(routeDistance) : this.taskForm.estimatedServiceMinutes
        const response = await getTaskEstimate({
          serviceType: this.taskForm.serviceType || 'HOME_SERVICE',
          estimatedRobocoin: this.taskForm.estimatedRobocoin,
          routeDistanceMeters: Number.isFinite(routeDistance) && routeDistance > 0 ? Number(routeDistance.toFixed(2)) : undefined,
          estimatedServiceMinutes: routeMinutes || undefined,
          beginLat: this.taskForm.beginLat || undefined,
          beginLng: this.taskForm.beginLng || undefined,
          endLat: this.taskForm.endLat || undefined,
          endLng: this.taskForm.endLng || undefined
        })
        if (response.data.code === 200 && response.data.data) {
          this.estimateInfo = {
            ...this.estimateInfo,
            ...response.data.data
          }
        }
      } catch (error) {
        this.estimateInfo = {
          ...this.estimateInfo,
          message: '预估接口暂不可用，当前使用默认预估。'
        }
      } finally {
        this.estimateLoading = false
      }
    },
    applyPlatformSubsidy() {
      this.taskForm.estimatedRobocoin = Number(this.estimatedRobocoin)
      this.taskForm.estimatedServiceMinutes = this.estimateInfo.estimatedServiceMinutes
      this.taskForm.estimateSource = this.estimateInfo.estimateSource
      this.taskForm.robocoinHourlyRate = this.estimateInfo.robocoinHourlyRate
      this.taskForm.routeDistanceMeters = this.estimateInfo.routeDistanceMeters
      this.taskForm.robocoinDistanceRate = this.estimateInfo.robocoinDistanceRate
      this.taskForm.prepayStatus = 'PAID'
      this.taskForm.paymentMethod = 'PLATFORM_SUBSIDY'
      if (this.$refs.taskFormRef) {
        this.$refs.taskFormRef.clearValidate('prepayStatus')
      }
      this.$message.success(`平台补助已记录：${this.estimatedRobocoin} RoboCoin`)
    },
    buildPayload() {
      return {
        id: this.editingId,
        publisherUserId: this.currentUser.id,
        taskName: this.taskForm.taskName,
        title: this.taskForm.title,
        message: this.taskForm.message,
        typeId: this.taskForm.typeId,
        serviceType: this.taskForm.serviceType,
        expectedRobotType: this.taskForm.expectedRobotType,
        allowSubcontract: this.taskForm.allowSubcontract === 1 ? 1 : 0,
        go2Action: this.taskForm.expectedRobotType === 'GO2' ? this.taskForm.go2Action : null,
        go2ActionMeters: this.taskForm.expectedRobotType === 'GO2' && ['MOVE_FORWARD', 'MOVE_BACKWARD', 'PATROL_INSPECTION'].includes(this.taskForm.go2Action) ? this.taskForm.go2ActionMeters : null,
        go2ActionSeconds: this.taskForm.expectedRobotType === 'GO2' && ['TURN_AROUND', 'PATROL_INSPECTION'].includes(this.taskForm.go2Action) ? this.taskForm.go2ActionSeconds : null,
        serviceAddress: this.taskForm.serviceType === 'HOME_SERVICE' ? this.taskForm.serviceAddress : null,
        serviceLat: this.taskForm.serviceType === 'HOME_SERVICE' ? this.taskForm.serviceLat : null,
        serviceLng: this.taskForm.serviceType === 'HOME_SERVICE' ? this.taskForm.serviceLng : null,
        contactPhone: this.taskForm.contactPhone,
        scheduleTime: this.taskForm.scheduleTime,
        beginAddress: this.taskForm.serviceType === 'SHUTTLE' ? this.taskForm.beginAddress : null,
        beginLat: this.taskForm.serviceType === 'SHUTTLE' ? this.taskForm.beginLat : null,
        beginLng: this.taskForm.serviceType === 'SHUTTLE' ? this.taskForm.beginLng : null,
        endAddress: this.taskForm.serviceType === 'SHUTTLE' ? this.taskForm.endAddress : null,
        endLat: this.taskForm.serviceType === 'SHUTTLE' ? this.taskForm.endLat : null,
        endLng: this.taskForm.serviceType === 'SHUTTLE' ? this.taskForm.endLng : null,
        estimatedRobocoin: Number(this.estimatedRobocoin),
        estimatedServiceMinutes: this.estimateInfo.estimatedServiceMinutes,
        estimateSource: this.estimateInfo.estimateSource,
        robocoinHourlyRate: this.estimateInfo.robocoinHourlyRate,
        routeDistanceMeters: this.taskForm.serviceType === 'SHUTTLE' ? this.estimateInfo.routeDistanceMeters : null,
        robocoinDistanceRate: this.taskForm.serviceType === 'SHUTTLE' ? this.estimateInfo.robocoinDistanceRate : null,
        prepayStatus: 'PAID',
        paymentMethod: 'PLATFORM_SUBSIDY'
      }
    },
    async loadMyTasks() {
      if (!this.currentUser || !this.currentUser.id) {
        this.myTasks = []
        return
      }

      try {
        const response = await getMyPublishedTasks(this.currentUser.id)
        if (response.data.code === 200) {
          const tasks = response.data.data
          this.myTasks = Array.isArray(tasks)
            ? tasks.filter(task => task && typeof task === 'object')
            : []
          if (!Array.isArray(tasks)) {
            console.warn('[TaskPublish] myPublishedTasks returned a non-array payload:', tasks)
          }
        } else {
          this.$message.error(response.data.msg || '任务加载失败')
        }
      } catch (error) {
        this.$message.error('任务加载失败')
      }
    },
    delay(ms) {
      return new Promise(resolve => window.setTimeout(resolve, ms))
    },
    showSubmitDialog(title, text) {
      this.submitDialogDone = false
      this.submitDialogTitle = title
      this.submitDialogText = text
      this.submitDialogVisible = true
    },
    completeSubmitDialog(title, text) {
      this.submitDialogDone = true
      this.submitDialogTitle = title
      this.submitDialogText = text
    },
    async submitTask() {
      if (!this.currentUser || !this.currentUser.id) {
        this.$message.error('未检测到登录用户')
        return
      }
      if (!this.isReviewStep) {
        const valid = await this.validateFields(this.stepFields(this.currentStep))
        if (!valid) {
          this.$message.warning('请先完成当前步骤')
          return
        }
        this.currentStep = this.guidedSteps.length
        this.maxVisitedStep = this.guidedSteps.length
        return
      }
      const allFields = this.guidedSteps
        .filter(item => !['payment', 'review'].includes(item.key))
        .flatMap(item => this.stepFields(this.stepNumber(item.key)))
      const ready = await this.validateFields(allFields)
      if (!ready) {
        this.$message.warning('清单里还有信息未完成，请点击修改补齐')
        return
      }

      const isEditing = !!this.editingId
      this.applyPlatformSubsidy()
      this.showSubmitDialog(isEditing ? '正在保存修改' : '正在发布需求', `正在记录平台补助 ${this.estimatedRobocoin} RoboCoin 并写入任务信息，请稍候。`)

      try {
        const payload = this.buildPayload()
        const response = this.editingId
          ? await updatePublishedTask(payload)
          : await publishTask(payload)
        await this.delay(900)

        if (response.data.code !== 200) {
          this.submitDialogVisible = false
          this.$message.error(response.data.msg || '任务提交失败')
          return
        }

        this.completeSubmitDialog(isEditing ? '任务修改成功' : '发布需求成功', isEditing ? '任务信息和平台补助记录已更新。' : '需求已发布，平台补助已记录，正在等待后续匹配。')
        await this.delay(900)
        this.submitDialogVisible = false
        const taskId = response.data.data && response.data.data.taskId
        this.resetForm()
        await this.loadMyTasks()
        if (!isEditing && taskId) {
          this.$message.success('Task published and auto-approved. Task ID: ' + taskId)
        }
      } catch (error) {
        this.submitDialogVisible = false
        console.error('[TaskPublish] submit failed:', error)
        this.$message.error(getApiErrorMessage(error, '任务提交失败，请检查任务信息后重试。'))
      }
    },
    startEdit(task) {
      this.editingId = task.id
      this.currentStep = this.guidedSteps.length
      this.maxVisitedStep = this.guidedSteps.length
      this.reviewEditingKey = ''
      this.taskForm = {
        taskName: task.taskName || '',
        title: task.title || '',
        message: task.message || '',
        typeId: task.typeId || 1,
        serviceType: task.serviceType || 'HOME_SERVICE',
        expectedRobotType: task.expectedRobotType || '',
        allowSubcontract: Number(task.allowSubcontract) === 1 ? 1 : 0,
        go2Action: task.go2Action || '',
        go2ActionMeters: task.go2ActionMeters || 1,
        go2ActionSeconds: task.go2ActionSeconds || null,
        serviceAddress: task.serviceAddress || '',
        serviceLat: task.serviceLat ?? '',
        serviceLng: task.serviceLng ?? '',
        contactPhone: task.contactPhone || '',
        scheduleTime: task.scheduleTime || '',
        beginAddress: task.beginAddress || '',
        beginLat: task.beginLat ?? '',
        beginLng: task.beginLng ?? '',
        endAddress: task.endAddress || '',
        endLat: task.endLat ?? '',
        endLng: task.endLng ?? '',
        estimatedRobocoin: task.estimatedRobocoin || 28,
        estimatedServiceMinutes: task.estimatedServiceMinutes || 120,
        estimateSource: task.estimateSource || 'DEFAULT',
        robocoinHourlyRate: task.robocoinHourlyRate || 1.5,
        routeDistanceMeters: task.routeDistanceMeters || null,
        robocoinDistanceRate: task.robocoinDistanceRate || 0,
        prepayStatus: 'PAID',
        paymentMethod: 'PLATFORM_SUBSIDY'
      }
      this.estimateInfo = {
        ...this.estimateInfo,
        estimatedRobocoin: this.taskForm.estimatedRobocoin,
        estimatedServiceMinutes: this.taskForm.estimatedServiceMinutes,
        estimateSource: this.taskForm.estimateSource,
        robocoinHourlyRate: this.taskForm.robocoinHourlyRate,
        routeDistanceMeters: this.taskForm.routeDistanceMeters,
        robocoinDistanceRate: this.taskForm.robocoinDistanceRate
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    async cancelTask(task) {
      try {
        await this.$confirm('Canceling stops new assignments. A cancelled task with no order can then be permanently deleted.', 'Cancel published task', {
          confirmButtonText: 'Cancel task',
          cancelButtonText: 'Keep task',
          type: 'warning'
        })
        const response = await cancelPublishedTask({
          taskId: task.id,
          userId: this.currentUser.id
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '任务取消失败')
          return
        }
        this.$message.success('任务已取消')
        this.loadMyTasks()
      } catch (error) {
        this.$message.error('任务取消失败')
      }
    },
    canDeleteTask(row) {
      if (!row || this.isTaskAccepted(row)) return false
      return String(row.publishStatus || '').toUpperCase() === 'CANCELLED'
    },
    async deleteTask(task) {
      try {
        await this.$confirm('This removes the cancelled task from your list permanently and cannot be undone.', 'Delete task', {
          confirmButtonText: 'Delete permanently',
          cancelButtonText: 'Keep task',
          type: 'warning'
        })
        const response = await deletePublishedTask({ taskId: task.id, userId: this.currentUser.id })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || 'Task deletion failed')
          return
        }
        this.$message.success('Task deleted')
        await this.loadMyTasks()
      } catch (error) {
        if (error === 'cancel' || error === 'close') return
        this.$message.error('Task deletion failed. Please try again later.')
      }
    },
    serviceTypeLabel(value) {
      const matched = this.serviceTypes.find(item => item.value === value)
      return matched ? matched.label : '未选择'
    },
    formatCoin(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(2) : '0.00'
    },
    formatDuration(minutes) {
      const total = Number(minutes)
      if (!Number.isFinite(total) || total <= 0) return '0 分钟'
      const hours = Math.floor(total / 60)
      const rest = Math.round(total % 60)
      if (hours <= 0) return `${rest} 分钟`
      if (rest <= 0) return `${hours} 小时`
      return `${hours} 小时 ${rest} 分钟`
    },
    prepayStatusLabel(value) {
      const map = {
        UNPAID: '未支付',
        PAID: '平台补助',
        PARTIAL_REFUNDED: '部分退款',
        DEDUCTED: '已扣费',
        REFUNDED: '已退款'
      }
      return map[value] || value || '未支付'
    },
    prepayStatusTag(value) {
      const map = {
        UNPAID: 'warning',
        PAID: 'success',
        PARTIAL_REFUNDED: 'warning',
        DEDUCTED: 'danger',
        REFUNDED: 'info'
      }
      return map[value] || 'info'
    },
    stepNumber(key) {
      const index = this.guidedSteps.findIndex(item => item.key === key)
      return index >= 0 ? index + 1 : this.guidedSteps.length
    },
    robotTypeLabel(value) {
      const matched = this.robotTypes.find(item => item.value === value)
      return matched ? matched.label : (value || '未选择')
    },
    go2ActionText() {
      const matched = this.go2ActionTypes.find(item => item.value === this.taskForm.go2Action)
      const label = matched ? matched.label : 'Not selected'
      if (['MOVE_FORWARD', 'MOVE_BACKWARD'].includes(this.taskForm.go2Action)) return `${label} ${this.taskForm.go2ActionMeters || 1} m`
      if (this.taskForm.go2Action === 'PATROL_INSPECTION') return `${label} ${this.taskForm.go2ActionMeters || 10} m + 360 deg + ${this.taskForm.go2ActionMeters || 10} m`
      if (this.taskForm.go2Action === 'TURN_AROUND') return `${label} ${this.taskForm.go2ActionSeconds || 4} s`
      return label
    },
    isTaskAccepted(row) {
      if (!row) return false
      return Number(row.status) === 1 || String(row.dispatchStatus || '').toUpperCase() === 'ACCEPTED'
    },
    canEditTask(row) {
      if (!row) return false
      if (this.isTaskAccepted(row)) return false
      return !['CANCELLED', 'FINISHED', 'REJECTED'].includes(String(row.publishStatus || '').toUpperCase())
    },
    canCancelTask(row) {
      if (!row) return false
      return !['CANCELLED', 'FINISHED', 'REJECTED'].includes(String(row.publishStatus || '').toUpperCase())
    },
    publishStatusLabel(row) {
      const value = row && row.publishStatus ? String(row.publishStatus).toUpperCase() : ''
      if (this.isTaskAccepted(row)) return '已接单'
      const map = {
        PENDING_REVIEW: '待审核',
        ACTIVE: '可接单',
        CANCELLED: '已取消',
        FINISHED: '已完成',
        REJECTED: '已驳回'
      }
      return map[value] || value || '未知状态'
    },
    publishStatusTag(row) {
      const value = row && row.publishStatus ? String(row.publishStatus).toUpperCase() : ''
      if (this.isTaskAccepted(row)) return 'warning'
      const map = {
        PENDING_REVIEW: 'warning',
        ACTIVE: 'success',
        CANCELLED: 'info',
        FINISHED: 'success',
        REJECTED: 'info'
      }
      return map[value] || 'info'
    },
    dispatchStatusText(row) {
      if (!row) return '等待同步'
      if (Number(row.status) === 1) return '已成功接单'
      if (row.publishStatus === 'CANCELLED') return '任务已取消'
      if (row.verificationStatus !== 'PASSED') return '等待审核通过'
      return row.dispatchStatusText || 'DeRAS正在寻找机器人'
    },
    dispatchProgressDesc(row) {
      if (!row) return '等待 DeRAS 同步派单状态'
      if (Number(row.status) === 1) {
        return row.dispatchRobotName ? `机器人「${row.dispatchRobotName}」已锁定任务` : '任务已经进入进行中订单'
      }
      if (row.dispatchStatus === 'PENDING') {
        const name = row.dispatchRobotName || '候选机器人'
        return row.dispatchCandidateSummary || `已并发邀请候选机器人，最新候选为 ${name}`
      }
      if (row.dispatchStatus === 'REJECTED') return '候选机器人拒绝，DeRAS正在尝试下一台'
      if (row.dispatchStatus === 'EXPIRED') return '候选机器人超时，DeRAS正在重新派单'
      if (row.dispatchCandidateCount > 0) return '暂无新候选，任务已开放给服务者抢单'
      if (row.verificationStatus !== 'PASSED') return row.verificationNote || '审核通过后会自动进入派单'
      return '类型、在线、空闲、信誉分会参与候选排序'
    }
  }
}
</script>

<style scoped>
.task-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  color: #eef4ff;
  background: linear-gradient(135deg, #07111f 0%, #0d1727 44%, #081527 100%);
}

.background-layer,
.glow,
.grid {
  position: absolute;
}

.background-layer {
  inset: 0;
  pointer-events: none;
}

.glow {
  border-radius: 50%;
  filter: blur(14px);
}

.glow-left {
  width: 34rem;
  height: 34rem;
  left: -10rem;
  top: -10rem;
  background: radial-gradient(circle, rgba(255, 154, 46, 0.18), transparent 68%);
}

.glow-right {
  width: 34rem;
  height: 34rem;
  right: -12rem;
  bottom: -12rem;
  background: radial-gradient(circle, rgba(62, 160, 255, 0.2), transparent 68%);
}

.grid {
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.9), transparent 96%);
}

.page-shell {
  position: relative;
  z-index: 1;
  padding: 24px 44px 44px;
}

.page-header,
.card-title-row,
.action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-header {
  margin-bottom: 16px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #ffb861;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
}

.page-header h1,
.card-title-row h3 {
  margin: 0;
  font-family: ui-serif, serif;
}

.page-header h1 {
  font-size: 42px;
}

.task-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 18px;
  width: min(1620px, 100%);
  margin: 0 auto;
}

.form-card,
.list-card,
.task-summary-card {
  border-radius: 8px;
  background: rgba(7, 17, 29, 0.84);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(18px);
  box-shadow: 0 28px 64px rgba(0, 0, 0, 0.24);
}

.form-card,
.list-card {
  min-width: 0;
  padding: 26px;
}

.publisher-workspace {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 18px;
  align-items: start;
  width: 100%;
  max-width: 100%;
}

.publisher-sidebar {
  display: grid;
  gap: 14px;
}

.task-summary-card {
  padding: 18px;
}

.summary-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.summary-head h2 {
  margin: 0;
  font-family: inherit;
  font-size: 18px;
  letter-spacing: 0;
}

.summary-head > span {
  display: grid;
  place-items: center;
  min-width: 34px;
  height: 34px;
  padding: 0 7px;
  border: 1px solid rgba(142, 231, 255, 0.2);
  border-radius: 8px;
  color: #8ee7ff;
  background: rgba(142, 231, 255, 0.08);
  font-size: 11px;
  font-weight: 800;
}

.summary-head .eyebrow {
  margin-bottom: 5px;
}

.summary-list {
  display: grid;
  margin-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.summary-item {
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.summary-item i {
  width: 7px;
  height: 7px;
  margin-top: 5px;
  border-radius: 50%;
  background: rgba(238, 244, 255, 0.24);
  box-shadow: 0 0 0 3px rgba(238, 244, 255, 0.05);
}

.summary-item i.ready {
  background: #74f2bf;
  box-shadow: 0 0 0 3px rgba(116, 242, 191, 0.1);
}

.summary-item span,
.summary-item strong {
  display: block;
}

.summary-item span {
  margin-bottom: 4px;
  color: rgba(238, 244, 255, 0.48);
  font-size: 11px;
}

.summary-item strong {
  display: -webkit-box;
  overflow: hidden;
  color: rgba(247, 251, 255, 0.9);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.45;
  word-break: break-word;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.mobile-task-summary {
  display: none;
}

.back-btn,
.ghost-btn {
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.prominent-back-btn {
  display: inline-grid;
  grid-template-columns: 34px auto;
  grid-template-rows: auto auto;
  column-gap: 10px;
  align-items: center;
  min-width: 188px;
  min-height: 58px;
  padding: 10px 16px;
  border-color: rgba(255, 179, 71, 0.55);
  color: #fff8ea;
  background:
    linear-gradient(135deg, rgba(255, 179, 71, 0.22), rgba(99, 214, 146, 0.1)),
    rgba(8, 14, 25, 0.82);
  box-shadow: 0 14px 32px rgba(255, 122, 24, 0.2);
}

.prominent-back-btn span {
  grid-row: 1 / span 2;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #09111c;
  background: linear-gradient(135deg, #ffb347, #ffd08a);
  font-size: 22px;
  font-weight: 900;
}

.prominent-back-btn strong,
.prominent-back-btn em {
  display: block;
  font-style: normal;
  line-height: 1.15;
}

.prominent-back-btn strong {
  font-size: 14px;
  letter-spacing: 0;
}

.prominent-back-btn em {
  margin-top: 3px;
  color: rgba(255, 246, 234, 0.72);
  font-size: 12px;
}

.form-card {
  min-width: 0;
}

.card-title-row h3 {
  font-size: 34px;
}

.ghost-btn,
.back-btn,
.primary-btn {
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.ghost-btn:disabled,
.primary-btn:disabled {
  opacity: 0.48;
  cursor: not-allowed;
}

.ghost-btn:hover,
.back-btn:hover,
.primary-btn:hover {
  transform: translateY(-2px);
}

.ghost-btn:disabled:hover,
.primary-btn:disabled:hover {
  transform: none;
}

.form-grid {
  display: grid;
  gap: 18px;
}

.progress-overview {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  margin-top: 18px;
  padding: 12px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.progress-overview span,
.progress-overview em {
  color: rgba(238, 244, 255, 0.48);
  font-size: 11px;
  font-style: normal;
}

.progress-overview strong {
  overflow: hidden;
  color: rgba(247, 251, 255, 0.9);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flow-progress {
  position: relative;
  display: grid;
  grid-template-columns: repeat(var(--step-count), minmax(36px, 1fr));
  gap: 4px;
  margin: 16px 0 30px;
}

.flow-progress::before {
  position: absolute;
  top: 18px;
  right: 18px;
  left: 18px;
  height: 1px;
  background: rgba(238, 244, 255, 0.12);
  content: '';
}

.flow-dot {
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  min-width: 0;
  min-height: 38px;
  padding: 0;
  border: 0;
  color: rgba(238, 244, 255, 0.72);
  background: transparent;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.2s ease;
}

.flow-dot span {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(238, 244, 255, 0.15);
  border-radius: 50%;
  color: rgba(238, 244, 255, 0.68);
  background: #07111d;
  box-shadow: 0 0 0 4px #081321;
  font-size: 12px;
  font-weight: 800;
  transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.flow-dot em {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  z-index: 3;
  padding: 4px 6px;
  border-radius: 5px;
  color: rgba(238, 244, 255, 0.72);
  background: rgba(5, 13, 22, 0.92);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.22);
  font-style: normal;
  font-size: 10px;
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transform: translate(-50%, -3px);
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.flow-dot.active {
  transform: translateY(-2px);
}

.flow-dot.active span {
  border-color: rgba(142, 231, 255, 0.72);
  color: #041019;
  background: #8ee7ff;
  transform: scale(1.06);
}

.flow-dot.done span {
  border-color: rgba(116, 242, 191, 0.5);
  color: #74f2bf;
  background: rgba(116, 242, 191, 0.12);
}

.flow-dot:hover:not(.locked) span,
.flow-dot:focus-visible:not(.locked) span {
  border-color: rgba(142, 231, 255, 0.54);
  color: #f7fbff;
}

.flow-dot.active em,
.flow-dot:hover:not(.locked) em,
.flow-dot:focus-visible:not(.locked) em {
  opacity: 1;
  transform: translate(-50%, 0);
}

.flow-dot:focus-visible {
  outline: none;
}

.flow-dot.locked {
  opacity: 0.32;
  cursor: not-allowed;
}

.flow-dot.locked:hover {
  transform: none;
}

.question-panel {
  min-height: 420px;
}

.step-heading {
  margin-bottom: 22px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(8, 14, 25, 0.54);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.step-heading span {
  display: inline-flex;
  margin-bottom: 10px;
  color: #ffb861;
  font-size: 12px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

.step-heading h4 {
  margin: 0;
  font-size: 22px;
}

.step-heading p {
  margin: 10px 0 0;
  color: rgba(238, 244, 255, 0.68);
  line-height: 1.8;
}

.choice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.choice-form-item :deep(.el-form-item__content) {
  display: block;
  width: 100%;
  line-height: normal;
}

.robot-choice-grid {
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.choice-rail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.choice-rail-head strong,
.choice-rail-head span {
  display: block;
}

.choice-rail-head strong {
  color: rgba(247, 251, 255, 0.92);
  font-size: 14px;
}

.choice-rail-head span {
  margin-top: 4px;
  color: rgba(238, 244, 255, 0.5);
  font-size: 12px;
}

.choice-rail-actions {
  display: flex;
  gap: 8px;
}

.choice-rail-actions button {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid rgba(142, 231, 255, 0.2);
  border-radius: 8px;
  color: rgba(238, 244, 255, 0.78);
  background: rgba(142, 231, 255, 0.06);
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;
}

.choice-rail-actions button:hover,
.choice-rail-actions button:focus-visible {
  border-color: rgba(142, 231, 255, 0.5);
  color: #f7fbff;
  background: rgba(142, 231, 255, 0.13);
  outline: none;
}

.robot-type-rail {
  grid-template-columns: none;
  grid-auto-flow: column;
  grid-auto-columns: minmax(220px, calc((100% - 32px) / 3));
  overflow-x: auto;
  padding: 4px 2px 12px;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  scrollbar-color: rgba(142, 231, 255, 0.28) transparent;
  scrollbar-width: thin;
}

.robot-type-rail .choice-card {
  height: 100%;
  scroll-snap-align: start;
}

.choice-card {
  min-height: 148px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 22px;
  text-align: left;
  color: #eef4ff;
  background: rgba(8, 14, 25, 0.64);
  cursor: pointer;
  transition: transform 0.22s ease, border-color 0.22s ease, background 0.22s ease;
}

.choice-card:hover {
  transform: translateY(-3px);
  border-color: rgba(255, 179, 71, 0.55);
  background: rgba(255, 179, 71, 0.12);
}

.choice-card.selected {
  transform: translateY(-3px);
  border-color: rgba(116, 231, 194, 0.92);
  background: linear-gradient(145deg, rgba(116, 231, 194, 0.2), rgba(255, 179, 71, 0.12));
  box-shadow: 0 0 0 2px rgba(116, 231, 194, 0.26), 0 22px 46px rgba(26, 213, 177, 0.18);
}

.choice-card.selected span {
  background: linear-gradient(135deg, #74e7c2, #ffd06f);
  color: #07121d;
}

.choice-card.selected strong {
  color: #ffffff;
}

.choice-card span {
  width: max-content;
  min-width: 56px;
  height: 46px;
  max-width: 100%;
  padding: 0 12px;
  box-sizing: border-box;
  margin-bottom: 14px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #09111c;
  white-space: nowrap;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
  background: linear-gradient(135deg, #ffb347, #ff7a18);
}

.choice-card strong {
  display: block;
  margin-bottom: 8px;
  font-size: 20px;
}

.choice-card p {
  margin: 0;
  color: rgba(238, 244, 255, 0.68);
  line-height: 1.7;
}

.go2-preset-summary {
  margin: 8px 0 18px;
  padding: 16px 18px;
  border: 1px solid rgba(116, 231, 194, 0.52);
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(116, 231, 194, 0.16), rgba(116, 181, 255, 0.1));
  box-shadow: 0 16px 34px rgba(32, 214, 175, 0.12);
  color: #eefcff;
}

.go2-preset-summary strong {
  display: block;
  margin-bottom: 6px;
  font-size: 15px;
  letter-spacing: 0;
}

.go2-preset-summary span {
  display: block;
  color: rgba(238, 252, 255, 0.76);
  line-height: 1.5;
}

.choice-card small {
  display: inline-flex;
  margin-top: 14px;
  padding: 6px 10px;
  border-radius: 999px;
  color: #0a111c;
  font-size: 12px;
  font-weight: 700;
  background: #ffd08a;
}

.single-field {
  max-width: 760px;
}

.payment-panel {
  display: grid;
  gap: 16px;
  min-width: 0;
  max-width: 100%;
}

.route-estimate-card {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 10px;
  padding: 14px;
  border-radius: 18px;
  background: rgba(8, 14, 25, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.route-estimate-card div {
  padding: 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
}

.route-estimate-card span,
.route-charge-list span {
  display: block;
  color: rgba(238, 244, 255, 0.58);
  font-size: 12px;
  letter-spacing: 0;
  text-transform: none;
}

.route-estimate-card strong {
  display: block;
  margin-top: 6px;
  color: #ffffff;
  font-size: 18px;
}

.route-estimate-card p {
  grid-column: 1 / -1;
  margin: 0;
  color: rgba(238, 244, 255, 0.66);
  line-height: 1.7;
}

.review-route-card {
  margin-top: 0;
}

.payment-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px;
  align-items: center;
  padding: 22px;
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(99, 214, 146, 0.14), rgba(255, 179, 71, 0.14)),
    rgba(8, 14, 25, 0.66);
  border: 1px solid rgba(255, 179, 71, 0.22);
}

.subcontract-policy {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.subcontract-policy button {
  min-height: 170px;
  padding: 22px;
  text-align: left;
  color: #eef4ff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(8, 14, 25, 0.62);
  cursor: pointer;
}

.subcontract-policy button.selected {
  border-color: rgba(99, 214, 146, 0.72);
  background: rgba(99, 214, 146, 0.12);
}

.subcontract-policy button:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.subcontract-policy span,
.subcontract-policy strong {
  display: block;
}

.subcontract-policy span {
  color: #8ed7ff;
  font-size: 12px;
}

.subcontract-policy strong {
  margin-top: 12px;
  font-size: 22px;
}

.subcontract-policy p {
  margin: 10px 0 0;
  color: rgba(238, 244, 255, 0.68);
  line-height: 1.7;
}

.payment-card span {
  display: block;
  margin-bottom: 8px;
  color: #ffb861;
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.payment-card strong {
  display: block;
  font-size: 38px;
  color: #fff;
}

.payment-card p,
.payment-rules p,
.payment-review-box p {
  margin: 8px 0 0;
  color: rgba(238, 244, 255, 0.7);
  line-height: 1.7;
}

.route-charge-list {
  display: grid;
  gap: 8px;
  min-width: 150px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
}

.pay-btn {
  min-width: 178px;
  height: 48px;
  border: none;
  border-radius: 999px;
  color: #09111c;
  font-weight: 800;
  cursor: pointer;
  background: linear-gradient(135deg, #63d692, #ffd08a);
  box-shadow: 0 18px 36px rgba(99, 214, 146, 0.18);
}

.pay-btn.paid {
  background: linear-gradient(135deg, #8ed7ff, #63d692);
}

.payment-rules {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.payment-rules div,
.payment-review-box {
  min-width: 0;
  padding: 16px;
  border-radius: 18px;
  background: rgba(8, 14, 25, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.payment-rules strong,
.payment-review-box strong {
  display: block;
  color: #fff;
}

.payment-rules p {
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.payment-review-box {
  display: grid;
  gap: 10px;
}

.payment-review-box button {
  justify-self: start;
}

.final-review {
  display: grid;
  gap: 18px;
}

.review-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 320px) auto;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(8, 14, 25, 0.58);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.review-head span {
  display: block;
  margin-bottom: 6px;
  color: #ffb861;
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.review-head h4 {
  margin: 0;
  font-size: 24px;
}

.review-head p {
  max-width: 320px;
  margin: 0;
  color: rgba(238, 244, 255, 0.68);
  line-height: 1.7;
}

.review-meter {
  min-width: 92px;
  padding: 12px 14px;
  border-radius: 16px;
  text-align: center;
  background: rgba(255, 179, 71, 0.12);
  border: 1px solid rgba(255, 179, 71, 0.22);
}

.review-meter strong {
  display: block;
  color: #ffd08a;
  font-size: 22px;
}

.review-meter em {
  color: rgba(238, 244, 255, 0.68);
  font-style: normal;
  font-size: 12px;
}

.task-page,
.task-layout,
.publisher-workspace,
.form-card,
.final-review,
.review-head,
.review-copy {
  min-width: 0;
}

@media (max-width: 1600px) {
  .page-shell {
    padding-right: 28px;
    padding-left: 28px;
  }

  .publisher-workspace {
    grid-template-columns: 220px minmax(0, 1fr);
  }

  .review-head {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .review-head p {
    grid-column: 1 / -1;
    max-width: 72ch;
  }
}

@media (max-width: 1240px) {
  .publisher-workspace {
    grid-template-columns: 1fr;
  }

  .publisher-sidebar {
    grid-template-columns: minmax(0, 1fr);
  }
}

.review-list {
  display: grid;
  gap: 12px;
}

.review-item {
  display: grid;
  gap: 0;
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(8, 14, 25, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: border-color 0.22s ease, background 0.22s ease;
}

.review-item.editing {
  border-color: rgba(255, 179, 71, 0.42);
  background: rgba(255, 179, 71, 0.08);
}

.review-item.incomplete {
  border-color: rgba(255, 122, 122, 0.34);
}

.review-main {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
}

.review-index {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  color: #0a111c;
  font-weight: 800;
  background: linear-gradient(135deg, #ffb347, #ff7a18);
}

.review-item span {
  display: block;
  margin-bottom: 6px;
  color: rgba(238, 244, 255, 0.58);
  font-size: 12px;
}

.review-item strong {
  display: block;
  color: #fff;
  line-height: 1.6;
  word-break: break-word;
}

.review-item button {
  height: 36px;
  padding: 0 14px;
  border: 1px solid rgba(255, 179, 71, 0.28);
  border-radius: 999px;
  color: #ffdca6;
  background: rgba(255, 179, 71, 0.1);
  cursor: pointer;
}

.review-editor {
  margin-top: 16px;
  padding: 16px;
  border-radius: 16px;
  background: rgba(8, 14, 25, 0.58);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.review-choice-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.robot-review-choices {
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
}

.mini-choice {
  min-height: 74px;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #eef4ff;
  text-align: left;
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.mini-choice:hover {
  transform: translateY(-2px);
  border-color: rgba(255, 179, 71, 0.48);
  background: rgba(255, 179, 71, 0.12);
}

.mini-choice.selected {
  transform: translateY(-2px);
  border-color: rgba(116, 231, 194, 0.88);
  background: rgba(116, 231, 194, 0.15);
  box-shadow: 0 0 0 2px rgba(116, 231, 194, 0.22);
}

.mini-choice:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.mini-choice span {
  width: max-content;
  min-width: 42px;
  height: 30px;
  max-width: 100%;
  padding: 0 9px;
  box-sizing: border-box;
  margin-bottom: 8px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0a111c;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
  background: linear-gradient(135deg, #ffb347, #ff7a18);
}

.mini-choice strong {
  font-size: 15px;
}

.review-field-grid {
  display: grid;
  gap: 12px;
}

.review-editor-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.review-done-btn,
.review-step-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 179, 71, 0.28);
  cursor: pointer;
}

.review-done-btn {
  color: #0a111c;
  font-weight: 700;
  background: linear-gradient(135deg, #ffb347, #ff7a18);
}

.review-step-btn {
  color: #ffdca6;
  background: rgba(255, 179, 71, 0.1);
}

.review-editor-enter-active,
.review-editor-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.review-editor-enter,
.review-editor-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.wizard-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.question-slide-enter-active,
.question-slide-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.question-slide-enter,
.question-slide-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.two-col {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.two-col > :deep(.el-form-item),
.two-col :deep(.el-form-item__content),
.two-col :deep(.address-picker) {
  min-width: 0;
}

.full-width {
  width: 100%;
}

.native-datetime-input {
  height: 48px;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  color: #eef4ff;
  background: rgba(8, 14, 25, 0.72);
  outline: none;
}

.native-datetime-input:focus {
  border-color: rgba(255, 179, 71, 0.72);
  box-shadow: 0 0 0 3px rgba(255, 179, 71, 0.14);
}

.native-datetime-input::-webkit-calendar-picker-indicator {
  filter: invert(1);
  cursor: pointer;
}

.task-form :deep(.el-form-item__label) {
  color: #f1dfc1;
  padding-bottom: 6px;
}

.task-form :deep(.el-input__inner),
.task-form :deep(.el-textarea__inner) {
  color: #fff;
  background: rgba(8, 14, 25, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
}

.task-form :deep(.el-radio-button__inner),
.task-form :deep(.el-select .el-input__inner) {
  color: #fff;
  background: rgba(8, 14, 25, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}

.task-form :deep(.el-radio-button__orig-radio:checked + .el-radio-button__inner) {
  background: linear-gradient(135deg, #ffb347, #ff7a18);
  border-color: transparent;
  box-shadow: none;
}

.action-row {
  margin-top: 8px;
}

.primary-btn {
  min-width: 160px;
  height: 48px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #ffb347, #ff7a18);
  color: #0a111c;
  font-weight: 700;
  box-shadow: 0 18px 36px rgba(255, 122, 24, 0.24);
}

:deep(.process-dialog) {
  border-radius: 24px;
  background: #111c2d;
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.36);
}

:deep(.process-dialog .el-dialog__header) {
  display: none;
}

:deep(.process-dialog .el-dialog__body) {
  padding: 34px 30px;
}

.process-content {
  text-align: center;
  color: #eef4ff;
}

.process-content i {
  display: inline-flex;
  margin-bottom: 18px;
  font-size: 48px;
}

.process-loading {
  color: #ffb347;
}

.process-success {
  color: #63d692;
}

.process-content h3 {
  margin: 0;
  font-size: 22px;
}

.process-content p {
  margin: 10px 0 0;
  color: rgba(238, 244, 255, 0.72);
  line-height: 1.7;
}

.task-table :deep(.el-table),
.task-table :deep(.el-table__expanded-cell) {
  --el-table-bg-color: transparent;
  --el-table-tr-bg-color: transparent;
  --el-table-header-bg-color: rgba(15, 31, 48, 0.92);
  --el-table-row-hover-bg-color: rgba(142, 231, 255, 0.08);
  --el-table-border-color: rgba(142, 231, 255, 0.14);
  --el-table-text-color: #eef4ff;
  --el-table-header-text-color: rgba(220, 239, 255, 0.75);
  background: rgba(4, 13, 24, 0.62);
  color: #eef4ff;
}

.task-table :deep(.el-table th),
.task-table :deep(.el-table tr),
.task-table :deep(.el-table td) {
  background: transparent;
  color: #eef4ff;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.danger-text {
  color: #ff9d77;
}

.dispatch-progress-cell {
  display: grid;
  gap: 4px;
  max-width: 250px;
}

.dispatch-progress-cell strong {
  color: #eef4ff;
  font-size: 13px;
}

.dispatch-progress-cell span {
  color: rgba(238, 244, 255, 0.62);
  font-size: 12px;
  line-height: 1.5;
}

.coin-unit {
  margin-left: 4px;
  color: rgba(238, 244, 255, 0.62);
  font-size: 12px;
}

.reveal-up {
  animation: revealUp 0.8s ease both;
}

.delay-1 {
  animation-delay: 0.1s;
}

.delay-2 {
  animation-delay: 0.2s;
}

.delay-3 {
  animation-delay: 0.3s;
}

@keyframes revealUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1080px) {
  .page-shell {
    padding-right: 24px;
    padding-left: 24px;
  }

  .two-col,
  .route-estimate-card,
  .payment-rules,
  .review-head,
  .review-item {
    grid-template-columns: 1fr;
  }

  .robot-type-rail {
    grid-auto-columns: minmax(220px, calc((100% - 16px) / 2));
  }
}

.task-table :deep(.el-table__empty-block),
.task-table :deep(.el-table__empty-text) {
  color: rgba(220, 239, 255, 0.68);
  background: rgba(4, 13, 24, 0.62);
}

.task-table :deep(.el-table__body-wrapper),
.task-table :deep(.el-table__header-wrapper) {
  max-width: 100%;
  background: rgba(4, 13, 24, 0.62);
}

@media (max-width: 900px) {
  .publisher-workspace {
    grid-template-columns: 1fr;
  }

  .publisher-sidebar {
    grid-template-columns: 220px minmax(0, 1fr);
  }
}

@media (max-width: 768px) {
  .page-shell {
    padding: 20px 16px 28px;
  }

  .page-header,
  .card-title-row,
  .action-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .publisher-sidebar {
    display: none;
  }

  .page-header h1 {
    font-size: 34px;
  }

  .card-title-row h3 {
    font-size: 26px;
  }

  .choice-rail-head {
    align-items: flex-start;
  }

  .robot-type-rail {
    grid-auto-columns: minmax(220px, 82vw);
  }

  .progress-overview {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .progress-overview em {
    display: none;
  }

  .flow-progress {
    grid-template-columns: repeat(var(--step-count), 36px);
    justify-content: start;
    overflow-x: auto;
    margin-bottom: 18px;
    padding: 0 4px 6px;
    scrollbar-width: thin;
  }

  .flow-progress::before,
  .flow-dot em {
    display: none;
  }

  .mobile-task-summary {
    display: block;
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  .wizard-actions {
    width: 100%;
  }

  .wizard-actions button {
    flex: 1;
  }

  .review-main,
  .review-choice-row,
  .robot-review-choices {
    grid-template-columns: 1fr;
  }

  .review-index {
    width: 38px;
    height: 38px;
  }

  .form-card,
  .list-card {
    padding: 18px;
    border-radius: 8px;
  }
}
</style>
